<template>
  <SimpleModal :is-open="state.shouldShow">
    <div class="flex flex-col gap-6 px-7 py-10 pb-6">
      <div class="flex items-center gap-3 text-gray-900 text-xl leading-6 font-semibold">
        <TransferIcon />
        <span>Line Item Swap</span>
      </div>
      <div class="flex items-center gap-4">
        <div class="flex-1">
          <span class="uppercase font-bold text-sm text-blue-400 block">
            {{ state.originalProductName }}
          </span>
          <span>
            {{ state.originalSubLotNum }}
          </span>
        </div>
        <ScanBox
          :completed="state.futureQuantity"
          :required="state.originalQuantity"
        />
      </div>

      <TabGroup v-if="state.availableToSwap > 0">
        <TabList
          class="border border-gray-400 rounded-lg overflow-hidden divide-x divide-gray-300"
          style="width: fit-content"
        >
          <Tab
            v-slot="{ selected }"
            class="relative text-sm"
          >
            <span
              class="h-full w-full block px-8 py-2"
              :class="selected ? 'shadow-inner bg-gray-200' : 'bg-gray-100'"
            >
              <span :class="{ 'text-gray-600': !selected }">Scan</span>
              <span
                aria-hidden="true"
                :class="[selected ? 'bg-blue-600 h-0.5 inset-x-2 border-blue-600 absolute bottom-0' : '']"
              ></span>
            </span>
          </Tab>
          <Tab
            v-slot="{ selected }"
            class="relative text-sm"
          >
            <span
              class="h-full w-full block px-8 py-2"
              :class="selected ? 'shadow-inner bg-gray-200' : 'bg-gray-100'"
            >
              <span :class="{ 'text-gray-600': !selected }">Search</span>
              <span
                aria-hidden="true"
                :class="[selected ? 'bg-blue-600 h-0.5 inset-x-2 border-blue-600 absolute bottom-0' : '']"
              ></span>
            </span>
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <div class="bg-blue-200 rounded-md py-6 px-8 text-sm flex gap-x-3">
              <img
                :src="asset('images/scan.svg')"
                width="22"
              />
              Begin Scanning
            </div>
          </TabPanel>
          <TabPanel>
            <div class="relative">
              <div class="flex items-center gap-4">
                <SearchInputController
                  :searchFunction="searchPackgesFn"
                  :selectedValue="state.selectedPackage?.sub_lot_num"
                  :auto-focus="true"
                  placeholder="Search packages"
                  @results="onSearchResults"
                  @blur="onSearchBlur"
                  @focus="onSearchFocus"
                  @error="onSearchError"
                  class="bg-gray-300 border-none rounded-lg p-2 flex-1"
                />
                <NumberStepper
                  v-model.number="state.selectedQuantity"
                  min="1"
                  :max="state.availableToSwap"
                  class="h-[36px] bg-gray-300 border-none rounded-lg w-full"
                />
                <button
                  type="button"
                  class="border-2 rounded-full w-6 h-6 flex items-center justify-center text-gray-600 border-gray-400 shrink-0"
                  @click="onAdd({ method: 'manual' })"
                >
                  <PlusIcon class="w-3 h-3" />
                </button>
              </div>

              <div v-if="state.packageSearchResults.length && state.shouldShowPackageSearchResults">
                <ol
                  class="fixed left-0 z-40 w-full bg-gray-100 overflow-y-auto border border-gray-300 border-t-0 rounded-lg rounded-t-none p-4 bg-white shadow-md mt-1"
                  style="max-height: 200px"
                >
                  <li
                    v-for="item in state.packageSearchResults"
                    :key="item.sub_lot_num"
                    class="px-3"
                  >
                    <button
                      @click="() => onSelectSearchResult(item)"
                      class="w-full py-2 text-left flex justify-between items-center border-b border-gray-300"
                    >
                      <span>
                        <span class="uppercase font-bold text-sm text-blue-400 block">
                          {{ item.product_name }}
                        </span>
                        <span class="text-xs">{{ item.sub_lot_num }}</span>
                      </span>
                      <span class="text-sm">
                        {{ item.quantity_available }}
                      </span>
                    </button>
                  </li>
                </ol>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </TabGroup>

      <div
        class="divider-wrapper"
        v-if="state.swapItems.length"
      >
        <div class="divider-inner">
          <ArrowPathRoundedSquareIcon class="h-7 w-7" />
        </div>
      </div>

      <ol
        class="flex flex-col gap-4"
        v-if="state.swapItems.length"
      >
        <li
          v-for="(item, index) in state.swapItems"
          :key="item.sub_lot_num"
          class="flex items-center gap-4"
        >
          <button
            type="button"
            class="border-2 rounded-full w-6 h-6 flex items-center justify-center text-gray-600 border-gray-400 shrink-0"
            @click="() => onDeleteSwapItem(index)"
          >
            <MinusIcon class="w-3 h-3" />
          </button>
          <span class="flex-1">
            <span class="uppercase font-bold text-sm text-blue-400 block">
              {{ state.originalProductName }}
            </span>
            <span>
              {{ item.sub_lot_num }}
            </span>
          </span>
          <CornerBox class="text-green-400">+{{ item.quantity }}</CornerBox>
        </li>
      </ol>

      <div class="flex gap-3 justify-end border-t-2 border-blue-600 pt-4">
        <button
          @click="onCancel"
          type="button"
          class="btn px-3 h-[42px] font-semibold uppercase border-none text-gray-600"
        >
          Cancel
        </button>
        <button
          @click="onCommitChanges"
          :disabled="!state.swapItems.length || fulfillmentService.swap.isSaving.value"
          type="button"
          class="btn btn-primary px-12 h-[42px] font-semibold uppercase"
        >
          Save
          <Spinner
            v-if="fulfillmentService.swap.isSaving.value"
            class="text-gray-100 ml-2"
          />
        </button>
      </div>
    </div>
  </SimpleModal>
</template>

<script setup>
import { inject, reactive, computed, watchEffect } from 'vue'
import SearchInputController from '@/Components/Search/SearchInputController'
import NumberStepper from '@/Components/Forms/NumberStepper'
import SimpleModal from '@/Components/Modals/SimpleModal.vue'
import TransferIcon from '@/Components/Icons/TransferIcon.vue'
import ScanBox from '@/Components/ScanBox.vue'
import CornerBox from '@/Components/CornerBox.vue'
import { PlusIcon, MinusIcon } from '@heroicons/vue/outline'
import { ArrowPathRoundedSquareIcon } from '@heroicons-2/vue/24/solid'
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/vue'
import { LANG, FULFILLMENT_SERVICE } from '@/Composables/Fulfillment/constants'
import { settle } from '@/utils/promises'
import { api } from '@/Composables/Fulfillment/fulfillment-service-api'
import Spinner from '@/Components/Icons/Spinner.vue'
import { useErrors } from '@/Composables/errors'

const fulfillmentService = inject(FULFILLMENT_SERVICE)

const { showError } = useErrors()

const state = reactive({
  shouldShow: false,

  originalSubLotNum: undefined,
  originalProductId: undefined,
  originalProductName: undefined,
  originalQuantity: undefined,
  originalQuantityFulfilled: undefined,

  selectedQuantity: 1,

  selectedPackage: undefined,

  swapItems: [],

  packageSearchResults: [],
  shouldShowPackageSearchResults: false,

  futureQuantity: computed(() => {
    return state.originalQuantityFulfilled + state.stagedQuantity
  }),

  stagedQuantity: computed(() => {
    return state.swapItems.reduce((acc, item) => {
      return acc + item.quantity
    }, 0)
  }),

  availableToSwap: computed(() => {
    const availableToFulfill = state.originalQuantity - state.originalQuantityFulfilled - state.stagedQuantity
    return state.selectedPackage
      ? Math.min(state.selectedPackage?.quantity_available ?? 0, availableToFulfill)
      : availableToFulfill
  })
})

const open = ({ productName, productId, subLotNum, quantity, quantityFulfilled }) => {
  state.shouldShow = true

  state.originalProductName = productName
  state.originalProductId = productId
  state.originalSubLotNum = subLotNum
  state.originalQuantity = quantity
  state.originalQuantityFulfilled = quantityFulfilled

  state.selectedQuantity = 1
  state.selectedPackage = undefined

  state.swapItems = []

  state.packageSearchResults = []
  state.shouldShowPackageSearchResults = false
}

const onSelectSearchResult = (item) => {
  state.selectedPackage = item
}

const onSearchError = (error) => {
  showError({ message: LANG.ERROR_SEARCHING_PACKAGES, useToast: true })
}

const searchPackgesFn = (...args) =>
  api.searchFulfillmentItems(fulfillmentService.order.id, state.originalProductId, ...args)

const onScanItem = async (subLotNum) => {
  const [err, result] = await settle(
    api.searchFulfillmentItems(fulfillmentService.order.id, state.originalProductId, subLotNum)
  )

  if (err) {
    await settle(showError({ message: LANG.ERROR_SEARCHING_PACKAGES, useToast: true }))
    return
  }

  const [status, json] = result

  if (status === 200 && json.data.length > 0) {
    const exactMatch = json.data.find((item) => item.sub_lot_num === subLotNum)

    if (exactMatch) {
      state.selectedPackage = exactMatch
      state.selectedQuantity = 1
      onAdd({ method: 'scan' })
      return
    }
  }
  await settle(showError({ message: LANG.NO_MATCHING_PACKAGE, useToast: true }))
}

watchEffect(() => {
  if (fulfillmentService.swap.toSwap.value) {
    open(fulfillmentService.swap.toSwap.value)
  }

  if (!fulfillmentService.swap.toSwap.value) {
    state.shouldShow = false
  }
})

watchEffect(() => {
  if (fulfillmentService.swap.scannedSwapItem.value) {
    onScanItem(fulfillmentService.swap.scannedSwapItem.value)
    fulfillmentService.swap.clearScannedSwapItem()
  }
})

const onAdd = async ({ method = 'scan' }) => {
  if (!state.selectedPackage || !state.selectedQuantity) {
    return
  }

  if (state.futureQuantity >= state.originalQuantity) {
    await settle(showError({ message: LANG.ERROR_QUANTITY_EXCEEDS_ORIGINAL, useToast: true }))
    return
  }

  if (state.selectedPackage.sub_lot_num === state.originalSubLotNum) {
    await settle(showError({ message: LANG.ERROR_SAME_SUBLOT, useToast: true }))
    return
  }

  state.swapItems.push(
    fulfillmentService.createItem({
      sub_lot_num: state.selectedPackage.sub_lot_num,
      original_sub_lot_num: state.originalSubLotNum,
      quantity: state.selectedQuantity,
      method
    })
  )

  console.log(state.swapItems)

  state.selectedPackage = undefined
  state.selectedQuantity = 1
  state.packageSearchResults = []
}

const onDeleteSwapItem = (index) => {
  state.swapItems.splice(index, 1)
}

const onCommitChanges = () => {
  fulfillmentService.swap.saveSwapItems(state.swapItems)
}

const onSearchResults = (results) => {
  const [_, json] = results
  state.packageSearchResults = json.data.filter((item) => item.quantity_available > 0)
}

const onSearchBlur = () => {
  state.shouldShowPackageSearchResults = false
}

const onSearchFocus = () => {
  state.shouldShowPackageSearchResults = true
}

const onCancel = () => {
  fulfillmentService.swap.clear()
}
</script>

<style scoped lang="pcss">
.divider-wrapper {
  background: linear-gradient(#efefef, #efefef) no-repeat center/100% 1px;
  @apply flex justify-center;
}
.divider-inner {
  @apply bg-gray-100 flex justify-center items-center px-3 text-gray-400;
}
</style>
